import React from 'react';
import {
  Alert, Button, ConfirmDialog, PageHeader, SegmentedControl,
} from '@parsina/framework';

import { useTranslation } from '../../i18n';
import { errorMessage, gql } from '../../lib/api';
import useDocumentTitle from '../../lib/useDocumentTitle';
import { useUrlState } from '../../lib/urlState';
import { useShortcut } from '../../lib/shortcuts';
import {
  ALERT_TARGET, DEFAULT_WINDOW, LEDGER_STATUSES, OWNER_TYPES, VIEWS, WINDOWS, num, sortAlerts,
  windowRange,
} from '../../lib/aiPricingView';
import { OVERVIEW_QUERY } from './queries';
import OverviewView from './OverviewView';
import ModelsView from './ModelsView';
import ServicesView from './ServicesView';
import PoliciesView from './PoliciesView';
import LedgerView from './LedgerView';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const dateParam = { default: '', parse: (raw) => (ISO_DATE.test(raw) ? raw : '') };
const flag = { default: '', values: ['', '1'] };

/** The URL contract (design §12.1). Defaults are absent from the address. */
const SCHEMA = {
  view: { default: 'overview', values: VIEWS },
  w: { default: DEFAULT_WINDOW, values: WINDOWS },
  owner: { default: '', values: ['', ...OWNER_TYPES] },
  q: { default: '' },
  sort: { default: '' },
  dir: { default: '', values: ['', 'asc', 'desc'] },
  page: { default: 1, parse: (raw) => Number(raw) || 1 },
  service: { default: '' },
  model: { default: '' },
  policy: { default: '' },
  from: { default: '' },
  inv: { default: '' },
  negative: flag,
  unpriced: flag,
  mismatch: flag,
  deprecated: flag,
  status: { default: '', values: ['', ...LEDGER_STATUSES] },
  charged: { default: '', values: ['', 'yes', 'no'] },
  ownerId: { default: '' },
  dfrom: dateParam,
  dto: dateParam,
};

/** Everything a view owns; switching views clears it, the window survives. */
const VIEW_LOCAL = {
  q: '', sort: '', dir: '', page: 1, service: '', model: '', policy: '', from: '', inv: '',
  negative: '', unpriced: '', mismatch: '', deprecated: '', status: '', charged: '', ownerId: '',
  dfrom: '', dto: '',
};

const SEVERITY_VARIANT = { danger: 'error', warning: 'warning', info: 'info' };

/**
 * «هزینه هوش مصنوعی و قیمت‌گذاری پارسه» — the container (design §12.1–12.2).
 *
 * ① PageHeader with the five views (`semantics="tabs"`) and the window
 * (`semantics="filter"`) · ② the alert zone, ordered by severity, fed by
 * `aiCostOverview.alerts` on EVERY view · ⑤ the view.
 *
 * The overview query lives here because its alerts do: an operator on the
 * ledger must still see «هیچ سیاست فعال نیست». It is fetched once per window
 * and handed to the overview view, which fetches nothing of its own.
 *
 * `go()` is the one way the URL changes from inside the screen, which is where
 * the policy editor's dirty guard hooks in: a tab press with unsaved parameters
 * asks first (§10).
 */
export default function AiPricing() {
  const { t, formatNumber, formatPercent } = useTranslation();
  useDocumentTitle('nav.title.aiPricing');

  const [view, setView] = useUrlState(SCHEMA);

  // `to` is "now" at the moment the window was chosen; re-deriving it on every
  // render would refetch forever.
  const window = React.useMemo(() => windowRange(view.w), [view.w]);

  const [overview, setOverview] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const loadOverview = React.useCallback(() => {
    setLoading(true);
    setError(null);
    gql(OVERVIEW_QUERY, { window, ownerType: view.owner || null })
      .then((d) => setOverview(d?.aiCostOverview || null))
      .catch((e) => setError(errorMessage(e, t)))
      .finally(() => setLoading(false));
  }, [window, view.owner, t]);

  React.useEffect(() => { loadOverview(); }, [loadOverview]);

  // ── dirty guard ────────────────────────────────────────────────────────────
  const [editorDirty, setEditorDirty] = React.useState(false);
  const [pendingPatch, setPendingPatch] = React.useState(null);

  const leavesEditor = (patch) => editorDirty && view.policy
    && (('view' in patch && patch.view !== 'policies') || ('policy' in patch && patch.policy !== view.policy));

  const go = React.useCallback((patch) => {
    if (leavesEditor(patch)) { setPendingPatch(patch); return; }
    setView(patch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setView, editorDirty, view.policy]);

  const changeView = React.useCallback((next) => {
    if (next === view.view) return;
    go({ ...VIEW_LOCAL, view: next });
  }, [go, view.view]);

  const goToAlert = (kind) => {
    const target = ALERT_TARGET[kind];
    if (!target) return;
    const { view: next, ...rest } = target;
    go({ ...VIEW_LOCAL, view: next, ...rest });
  };

  // `1`–`5` switch views; bare digits never fire while typing (registry rule).
  VIEWS.forEach((name, i) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useShortcut(String(i + 1), () => changeView(name), {
      label: t('shortcuts.action.view', { view: t(`aiPricing.views.${name}`) }),
      group: t('shortcuts.groups.aiPricing'),
    });
  });

  const alerts = React.useMemo(() => sortAlerts(overview?.alerts), [overview]);

  const alertText = (alert) => {
    const detail = alert.detail == null ? '' : String(alert.detail);
    switch (alert.kind) {
      case 'provider_drift':
        return t('aiPricing.alerts.provider_drift', { pct: num(detail) == null ? detail : formatPercent(num(detail)) });
      case 'wallet_value_changed': {
        const [from, to] = detail.split(/\s*(?:->|→|←|,|;|\|)\s*/);
        const fmt = (v) => (num(v) == null ? (v || t('common.value.none')) : formatNumber(num(v)));
        return t('aiPricing.alerts.wallet_value_changed', { from: fmt(from), to: fmt(to ?? from) });
      }
      case 'no_active_policy':
      case 'unpriced_models':
      case 'negative_margin':
      case 'apps_mismatch':
      case 'telemetry_dropped':
      case 'deprecated_in_use':
        return t(`aiPricing.alerts.${alert.kind}`, { count: alert.count ?? 0 });
      default:
        // A kind this build has not been taught is echoed, not swallowed.
        return detail || alert.kind;
    }
  };

  const viewProps = { window, view, go };

  return (
    <div className="relative flex h-full flex-col bg-background" data-testid="ai-pricing">
      <PageHeader title={t('aiPricing.title')} sub={t('aiPricing.sub')}>
        <SegmentedControl
          semantics="tabs"
          size="sm"
          ariaLabel={t('aiPricing.views.label')}
          options={VIEWS.map((value) => ({ value, label: t(`aiPricing.views.${value}`) }))}
          value={view.view}
          onChange={changeView}
          data-testid="ai-pricing-tabs"
        />
        <SegmentedControl
          semantics="filter"
          size="sm"
          ariaLabel={t('aiPricing.window.label')}
          options={WINDOWS.map((value) => ({ value, label: t(`aiPricing.window.d${value}`) }))}
          value={view.w}
          onChange={(w) => go({ w, page: 1 })}
          data-testid="ai-pricing-window"
        />
      </PageHeader>

      <div className="min-h-0 flex-1 overflow-y-auto px-8 py-6">
        <div className="mx-auto w-full max-w-[1440px]">
          {/* ② Alert zone — every alert is a door into the view that can fix it. */}
          {!view.policy ? (
            <div className="mb-5 flex flex-col gap-3" aria-label={t('aiPricing.alerts.label')} data-testid="ai-pricing-alerts">
              {error && view.view !== 'overview' ? (
                <Alert
                  variant="error"
                  data-testid="ai-pricing-alerts-error"
                  action={<Button variant="outline" size="sm" onClick={loadOverview}>{t('common.actions.retry')}</Button>}
                >
                  {t('aiPricing.overview.error')}
                </Alert>
              ) : null}
              {alerts.map((alert) => (
                <Alert
                  key={alert.kind}
                  variant={SEVERITY_VARIANT[alert.severity] || 'info'}
                  data-testid={`ai-pricing-alert-${alert.kind}`}
                  data-severity={alert.severity}
                  action={ALERT_TARGET[alert.kind] ? (
                    <Button variant="outline" size="sm" onClick={() => goToAlert(alert.kind)}>
                      {t('aiPricing.alerts.view')}
                    </Button>
                  ) : undefined}
                >
                  {alertText(alert)}
                </Alert>
              ))}
            </div>
          ) : null}

          <div role="tabpanel" aria-label={t(`aiPricing.views.${view.view}`)} data-testid={`ai-pricing-panel-${view.view}`}>
            {view.view === 'overview' ? (
              <OverviewView {...viewProps} overview={overview} loading={loading} error={error} onRetry={loadOverview} />
            ) : view.view === 'models' ? (
              <ModelsView {...viewProps} />
            ) : view.view === 'services' ? (
              <ServicesView {...viewProps} />
            ) : view.view === 'policies' ? (
              <PoliciesView {...viewProps} onEditorDirty={setEditorDirty} />
            ) : (
              <LedgerView {...viewProps} />
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={!!pendingPatch}
        onOpenChange={(open) => { if (!open) setPendingPatch(null); }}
        title={t('aiPricing.policies.editor.dirty.title')}
        description={t('aiPricing.policies.editor.dirty.body')}
        confirmLabel={t('aiPricing.policies.editor.dirty.confirm')}
        cancelLabel={t('aiPricing.policies.editor.dirty.cancel')}
        destructive
        onConfirm={() => { const patch = pendingPatch; setPendingPatch(null); setEditorDirty(false); setView(patch); }}
        data-testid="ai-pricing-dirty-guard"
        confirmTestId="ai-pricing-dirty-guard-confirm"
        cancelTestId="ai-pricing-dirty-guard-cancel"
      />
    </div>
  );
}
